// 共通ユーティリティ: 学名の著者配列抽出とラベル整形
const formatAuthorsList = (names = []) => {
  if (!names.length) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} & ${names.at(-1)}`;
};

// 著者リレーションから "姓" 優先で氏名を抽出
export function extractAuthorNames(row = {}) {
  // すでに配列があればそのまま使う
  if (Array.isArray(row._authority_authors) && row._authority_authors.length) {
    return row._authority_authors.filter(Boolean);
  }
  if (Array.isArray(row.authors_arr) && row.authors_arr.length) {
    return row.authors_arr.filter(Boolean);
  }

  // scientific_name_and_author から researcher 情報を拾う
  if (Array.isArray(row.scientific_name_and_author)) {
    const names = row.scientific_name_and_author
      .sort((a, b) => (a.author_order ?? 0) - (b.author_order ?? 0))
      .map((rel) =>
        rel.researcher?.last_name ||
        rel.researcher?.last_name_eng ||
        rel.researcher?.first_name ||
        rel.researchers?.last_name ||
        rel.researchers?.last_name_eng ||
        rel.researchers?.first_name ||
        ""
      )
      .filter(Boolean);
    if (names.length) return names;
  }

  // authors_str があれば分割
  if (typeof row.authors_str === "string" && row.authors_str.trim()) {
    const names = row.authors_str
      .split(/[;,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (names.length) return names;
  }

  return [];
}

// 学名の簡易ラベル（ランク＋親＋著者/年）
export function formatScientificNameLabel(node = {}) {
  const rank = node.current_rank || "";
  const parentName = node._parent_label || "";
  const main = node.name_spell_valid || node.name_spell_original || node.id || "";
  const authors = extractAuthorNames(node);
  const authorYear = node.authority_year || "";
  const gbifAuth = node.authority_from_gbif || "";
  const suffixParts = [];
  if (authors.length) {
    suffixParts.push(formatAuthorsList(authors));
    if (authorYear) suffixParts.push(authorYear);
  } else if (gbifAuth) {
    suffixParts.push(`${gbifAuth} [auto filled data]`);
  } else if (authorYear) {
    suffixParts.push(authorYear);
  }
  const suffix = suffixParts.length ? ` [${suffixParts.join(", ")}]` : "";
  return `${rank ? `${rank}: ` : ""}${parentName ? `${parentName} ` : ""}${main}${suffix}`;
}

// 種・亜種の著者名を括弧に入れるべきか判定する
export function shouldBracketAuthorship(sn = {}, allNames = []) {
  const rankNorm = (sn.current_rank || sn.rank || "").toString().trim().toLowerCase();
  if (rankNorm !== "species" && rankNorm !== "subspecies" && rankNorm !== "replacement_name") return false;
  if (!sn.original_parent || !sn.current_parent) return false;

  const normalizeRank = (rank) => (rank || "").toString().trim().toLowerCase();
  const isGenus = (rank) => normalizeRank(rank) === "genus";

  const map =
    allNames instanceof Map
      ? allNames
      : Array.isArray(allNames)
        ? new Map(allNames.map((n) => [n.id, n]))
        : new Map();

  const resolveGenusAncestor = (parentId) => {
    if (!parentId) return null;
    const parent = map.get(parentId);
    if (!parent) return null;
    if (isGenus(parent.current_rank) || isGenus(parent.original_rank)) return parent;

    const candidates = [parent.current_parent, parent.original_parent].filter(Boolean);
    for (const cid of candidates) {
      const candidate = map.get(cid);
      if (candidate && (isGenus(candidate.current_rank) || isGenus(candidate.original_rank))) {
        return candidate;
      }
    }
    return null;
  };

  const originalGenus = resolveGenusAncestor(sn.original_parent);
  const currentGenus = resolveGenusAncestor(sn.current_parent);

  if (originalGenus && currentGenus) {
    const sameId = originalGenus.id && currentGenus.id && originalGenus.id === currentGenus.id;
    if (sameId) return false;
    const nameOf = (n) => (n.name_spell_valid || n.name_spell_original || "").toString().toLowerCase();
    const sameName = nameOf(originalGenus) && nameOf(originalGenus) === nameOf(currentGenus);
    return !sameName;
  }

  // Fallback to original-parent vs current-parent difference when genusを特定できない場合
  return sn.original_parent !== sn.current_parent;
}
